'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@radix-ui/react-icons';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { DateInput } from '../date-input/data-input';

const formatDate = (date, locale = 'en-us') => {
  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const getDateAdjustedForTimezone = (dateInput) => {
  if (typeof dateInput === 'string') {
    const parts = dateInput.split('-').map((part) => parseInt(part, 10));
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date;
  } else {
    return dateInput;
  }
};

const PRESETS = {
  current: [
    { name: 'today', label: 'Today' },
    { name: 'yesterday', label: 'Yesterday' },
  ],
  previous: [
    { name: 'last-week', label: 'Last Week' },
    { name: 'last-month', label: 'Last Month' },
  ],
  custom: [{ name: 'custom', label: 'Custom Date' }],
};

export const SingleDatePicker = ({
  defaultPreset = 'today',
  initialDate = new Date(new Date().setHours(0, 0, 0, 0)),
  onUpdate,
  align = 'end',
  locale = 'en-US',
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  // Helper function to parse date string in YYYY-MM-DD format
  const parseDateString = (dateString) => {
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed
    }
    return new Date(dateString);
  };

  // Determine if defaultPreset is a custom date string
  const isCustomDateString =
    typeof defaultPreset === 'string' && defaultPreset.includes('-');

  // Set initial date based on defaultPreset type
  const getInitialDate = () => {
    if (isCustomDateString) {
      return parseDateString(defaultPreset);
    }
    return getDateAdjustedForTimezone(initialDate);
  };

  const [selectedDate, setSelectedDate] = useState(getInitialDate());
  const openedDateRef = useRef();

  // Track the original date when popover opens
  const [originalDate, setOriginalDate] = useState(null);
  const [originalSelectedPreset, setOriginalSelectedPreset] = useState(null);

  // Set selectedPreset to 'custom' if defaultPreset is a date string, otherwise use the string value
  const [selectedPreset, setSelectedPreset] = useState(
    isCustomDateString ? 'custom' : defaultPreset
  );

  const [isSmallScreen, setIsSmallScreen] = useState(true);

  // Apply preset date on component mount if defaultPreset is not 'custom' and not a date string
  useEffect(() => {
    if (defaultPreset !== 'custom' && !isCustomDateString) {
      const presetDate = getPresetDate(defaultPreset);
      setSelectedDate(presetDate);
    }
  }, [defaultPreset, isCustomDateString]);

  // Keep internal state in sync when parent-provided initialDate changes
  useEffect(() => {
    setSelectedDate(getDateAdjustedForTimezone(initialDate));
    setSelectedPreset('custom');
  }, [initialDate]);

  useEffect(() => {
    const handleResize = () => {
      // setIsSmallScreen(window.innerWidth < 960);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const getPresetDate = (presetName) => {
    const preset = Object.values(PRESETS)
      .flat()
      .find(({ name }) => name === presetName);
    if (!preset) throw new Error(`Unknown date preset: ${presetName}`);

    const date = new Date();

    switch (preset.name) {
      case 'today':
        date.setHours(0, 0, 0, 0);
        break;
      case 'yesterday':
        date.setDate(date.getDate() - 1);
        date.setHours(0, 0, 0, 0);
        break;
      case 'last-week':
        date.setDate(date.getDate() - 7);
        date.setHours(0, 0, 0, 0);
        break;
      case 'last-month':
        date.setMonth(date.getMonth() - 1);
        date.setHours(0, 0, 0, 0);
        break;
    }

    return date;
  };

  const setPreset = (preset) => {
    setSelectedPreset(preset);
    const date = getPresetDate(preset);
    setSelectedDate(date);
  };

  const resetValues = () => {
    // Restore the original state that was saved when popover opened
    if (originalDate) {
      setSelectedDate(originalDate);
    }
    if (originalSelectedPreset !== null) {
      setSelectedPreset(originalSelectedPreset);
    }
  };

  const PresetButton = ({ preset, label, isSelected }) => (
    <Button
      className={cn(isSelected && 'pointer-events-none')}
      variant='ghost'
      onClick={() => {
        setPreset(preset);
      }}
    >
      <>
        <span className={cn('pr-2 opacity-0', isSelected && 'opacity-70')}>
          <CheckIcon width={18} height={18} />
        </span>
        {label}
      </>
    </Button>
  );

  const areDatesEqual = (a, b) => {
    if (!a || !b) return a === b;
    return a.getTime() === b.getTime();
  };

  useEffect(() => {
    if (isOpen) {
      // Save the current state as original when popover opens
      setOriginalDate(new Date(selectedDate));
      setOriginalSelectedPreset(selectedPreset);

      openedDateRef.current = selectedDate;
    }
  }, [isOpen]);

  return (
    <Popover
      modal={true}
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          resetValues();
        }
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button size={'lg'} variant='outline' disabled={disabled}>
          <div className='mr-2 flex items-center'>
            <CalendarIcon className='mr-2' width={15} height={15} />
            <span>Date:</span>
          </div>
          <div className='text-right'>
            <div className='py-1'>
              {selectedPreset === 'custom' ? (
                <div>{formatDate(selectedDate, locale)}</div>
              ) : (
                <div>
                  {
                    Object.values(PRESETS)
                      .flat()
                      .find((preset) => preset.name === selectedPreset)?.label
                  }
                </div>
              )}
            </div>
          </div>
          <div className='-mr-2 scale-125 pl-1 opacity-60'>
            {isOpen ? (
              <ChevronUpIcon width={24} />
            ) : (
              <ChevronDownIcon width={24} />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className='w-auto'>
        <div className='flex py-2'>
          <div className='flex'>
            <div className='flex flex-col'>
              <div className='flex flex-col items-center justify-end gap-2 px-3 pb-4 lg:flex-row lg:items-start lg:pb-0'>
                <div className='flex flex-col gap-2'>
                  <div className='flex gap-2'>
                    <DateInput
                      value={selectedDate}
                      onChange={(date) => {
                        setSelectedDate(date);
                        setSelectedPreset('custom');
                      }}
                    />
                  </div>
                </div>
              </div>
              {isSmallScreen && (
                <Select
                  defaultValue={selectedPreset}
                  onValueChange={(value) => {
                    setPreset(value);
                  }}
                  value={selectedPreset}
                >
                  <SelectTrigger className='mx-auto mb-2 mt-1 w-[180px]'>
                    <SelectValue placeholder={'Select...'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel className='pl-4'>Current</SelectLabel>
                      <SelectSeparator />
                      {PRESETS.current.map((preset) => (
                        <SelectItem key={preset.name} value={preset.name}>
                          {preset.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel className='pl-4'>Previous</SelectLabel>
                      <SelectSeparator />
                      {PRESETS.previous.map((preset) => (
                        <SelectItem key={preset.name} value={preset.name}>
                          {preset.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel className='pl-4'>Custom</SelectLabel>
                      <SelectSeparator />
                      {PRESETS.custom.map((preset) => (
                        <SelectItem key={preset.name} value={preset.name}>
                          {preset.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectSeparator />
                  </SelectContent>
                </Select>
              )}
              <div>
                <Calendar
                  mode='single'
                  onSelect={(date) => {
                    if (date) {
                      setSelectedDate(date);
                      setSelectedPreset('custom');
                    }
                  }}
                  selected={selectedDate}
                  numberOfMonths={1}
                  defaultMonth={selectedDate}
                />
              </div>
            </div>
          </div>
        </div>
        <div className='flex justify-end gap-2 py-2 pr-4'>
          <Button
            onClick={() => {
              setIsOpen(false);
              resetValues();
            }}
            variant='ghost'
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsOpen(false);
              if (!areDatesEqual(selectedDate, openedDateRef.current)) {
                onUpdate?.({ date: selectedDate, selectedPreset });
              }
            }}
          >
            Update
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
